// src/pages/StockTransfersPage.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Package, Truck, CheckCircle, ChevronDown, ChevronUp, DollarSign, CreditCard, Edit2, History, Printer } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { stockTransferApi, productApi } from '../api';
import CreateDialog from '../components/transfers/CreateDialog';
import EditDialog from '../components/transfers/EditDialog';
import LogsDialog from '../components/transfers/LogsDialog';
import ReceiveDialog from '../components/transfers/ReceiveDialog';
import PayDialog from '../components/transfers/PayDialog';
import { printTransfer } from '../components/transfers/printTransfer';

const STATUS_CONFIG = {
  pending:         { label: '待确认',  color: 'bg-yellow-100 text-yellow-700' },
  shipped:         { label: '已发货',  color: 'bg-blue-100 text-blue-700' },
  received:        { label: '已收货',  color: 'bg-purple-100 text-purple-700' },
  payment_pending: { label: '待收款',  color: 'bg-orange-100 text-orange-700' },
  completed:       { label: '已完成',  color: 'bg-green-100 text-green-700' },
  cancelled:       { label: '已取消',  color: 'bg-gray-100 text-gray-500' },
};

const StockTransfersPage = () => {
  const { currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const isManager = currentUser?.role === 'manager';
  const isBranch = !isHQ;

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedNo, setExpandedNo] = useState(null);
  const [detailMap, setDetailMap] = useState({});
  const [products, setProducts] = useState([]);

  // 弹窗状态
  const [showCreate, setShowCreate] = useState(false);
  const [editState, setEditState] = useState(null);   // { transfer_no, items }
  const [logsState, setLogsState] = useState(null);   // { transfer_no, logs, loading }
  const [receiveState, setReceiveState] = useState(null); // { transfer_no, items }
  const [payState, setPayState] = useState(null);     // { transfer_no, amount }

  useEffect(() => {
    loadTransfers();
    loadHQProducts();
  }, [filterStatus]);

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const res = await stockTransferApi.getList(filterStatus ? { status: filterStatus } : {});
      if (res.success) setTransfers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadHQProducts = async () => {
    try {
      const res = await productApi.getHQStock();
      if (res.success) setProducts(res.data);
    } catch (err) { console.error(err); }
  };

  const toggleDetail = async (transfer_no) => {
    if (expandedNo === transfer_no) { setExpandedNo(null); return; }
    setExpandedNo(transfer_no);
    if (!detailMap[transfer_no]) {
      try {
        const res = await stockTransferApi.getDetail(transfer_no);
        if (res.success) setDetailMap(prev => ({ ...prev, [transfer_no]: res.data }));
      } catch (err) { console.error(err); }
    }
  };

  const refreshTransfer = (transfer_no) => {
    loadTransfers();
    setDetailMap(prev => { const n = { ...prev }; delete n[transfer_no]; return n; });
  };

  // 创建
  const handleCreate = async (data) => {
    const res = await stockTransferApi.create(data);
    if (res.success) { setShowCreate(false); loadTransfers(); }
    else throw new Error(res.message || '创建失败');
  };

  // 编辑
  const openEdit = async (transfer_no) => {
    const res = await stockTransferApi.getDetail(transfer_no);
    if (res.success) setEditState({ transfer_no, items: res.data });
  };

  const handleEdit = async (transfer_no, data) => {
    const res = await stockTransferApi.edit(transfer_no, data);
    if (res.success) { setEditState(null); refreshTransfer(transfer_no); }
    else throw new Error(res.message || '编辑失败');
  };

  // 修改日志
  const openLogs = async (transfer_no) => {
    setLogsState({ transfer_no, logs: [], loading: true });
    try {
      const res = await stockTransferApi.getLogs(transfer_no);
      if (res.success) setLogsState({ transfer_no, logs: res.data, loading: false });
    } catch (err) { setLogsState(prev => ({ ...prev, loading: false })); }
  };

  // 发货
  const handleShip = async (transfer_no) => {
    if (!window.confirm(`确认发货 ${transfer_no}？发货后将扣减总店库存。`)) return;
    const res = await stockTransferApi.ship(transfer_no);
    if (res.success) { alert('发货成功'); refreshTransfer(transfer_no); }
    else alert(res.message || '发货失败');
  };

  // 收货
  const openReceive = async (transfer_no) => {
    const res = await stockTransferApi.getDetail(transfer_no);
    if (res.success) {
      setReceiveState({
        transfer_no,
        items: res.data.map(t => ({
          transfer_id: t.id, product_name: t.product_name,
          quantity: t.quantity, actual_quantity: t.quantity,
          box_quantity: t.box_quantity, box_count: t.box_count, transfer_unit: t.transfer_unit,
        }))
      });
    }
  };

  const handleReceive = async (transfer_no, data) => {
    const res = await stockTransferApi.receive(transfer_no, data);
    if (res.success) { alert('已确认收货，请提交付款通知'); setReceiveState(null); refreshTransfer(transfer_no); }
    else throw new Error(res.message || '收货失败');
  };

  // 付款
  const handlePay = async (transfer_no, data) => {
    const res = await stockTransferApi.pay(transfer_no, data);
    if (res.success) { alert('已通知总店，等待确认收款'); setPayState(null); refreshTransfer(transfer_no); }
    else throw new Error(res.message || '提交失败');
  };

  // 确认收款
  const handleConfirmPayment = async (transfer_no) => {
    if (!window.confirm(`确认已收款 ${transfer_no}？确认后分店库存将自动更新。`)) return;
    const res = await stockTransferApi.confirmPayment(transfer_no);
    if (res.success) { alert('已确认收款，分店库存已更新'); refreshTransfer(transfer_no); }
    else alert(res.message || '操作失败');
  };

  // 取消
  const handleCancel = async (transfer_no) => {
    if (!window.confirm('确定取消该调货申请？')) return;
    const res = await stockTransferApi.cancel(transfer_no);
    if (res.success) { alert('已取消'); refreshTransfer(transfer_no); }
    else alert(res.message || '取消失败');
  };

  // 打印
  const handlePrint = async (transfer_no) => {
    let details = detailMap[transfer_no];
    if (!details) {
      const res = await stockTransferApi.getDetail(transfer_no);
      if (res.success) { details = res.data; setDetailMap(prev => ({ ...prev, [transfer_no]: details })); }
    }
    let logs = [];
    try { const lr = await stockTransferApi.getLogs(transfer_no); if (lr.success) logs = lr.data; } catch (e) {}
    const transfer = transfers.find(t => t.transfer_no === transfer_no);
    printTransfer(transfer, details, logs);
  };

  const canEdit = (transfer) => {
    if (transfer.status !== 'pending') return false;
    if (isHQ) return true;
    if (isManager && isBranch && transfer.to_store_id === currentUser?.store_id) return true;
    return false;
  };

  const filterTabs = isHQ
    ? [['', '全部'], ['pending', '待确认'], ['shipped', '已发货'], ['payment_pending', '待收款'], ['completed', '已完成']]
    : [['', '全部'], ['pending', '待确认'], ['shipped', '待收货'], ['received', '待付款'], ['payment_pending', '已付款'], ['completed', '已完成']];

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      {/* 头部 */}
<div className="flex justify-between items-center">
  <div>
    <h2 className="text-xl font-bold">库存划拨</h2>
    <p className="text-xs text-gray-600 mt-1">{isHQ ? '管理所有门店的调货申请' : '向总店申请调货'}</p>
  </div>
  <div className="flex items-center gap-2 flex-wrap">
    {filterTabs.map(([val, label]) => (
      <button key={val} onClick={() => setFilterStatus(val)}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          filterStatus === val ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
        {label}
      </button>
    ))}
    {isManager && isBranch && (
      <button onClick={() => setShowCreate(true)}
        className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 transition-colors">
        <Plus className="w-4 h-4" /><span>发起调货申请</span>
      </button>
    )}
  </div>
</div>

      {/* 状态筛选 */}
      {/* <div className="flex flex-wrap gap-2">
        {filterTabs.map(([val, label]) => (
          <button key={val} onClick={() => setFilterStatus(val)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filterStatus === val ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div> */}

      {/* 划拨单列表 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">加载中...</p>
        </div>
      ) : transfers.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无划拨记录</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transfers.map(transfer => {
            const statusCfg = STATUS_CONFIG[transfer.status] || STATUS_CONFIG.pending;
            const isExpanded = expandedNo === transfer.transfer_no;
            const details = detailMap[transfer.transfer_no];

            return (
              <div key={transfer.transfer_no} className="bg-white rounded-xl shadow overflow-hidden">
                <div className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleDetail(transfer.transfer_no)}>
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-2 rounded-lg"><Truck className="w-5 h-5 text-blue-600" /></div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{transfer.transfer_no}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                        {transfer.is_modified == 1 && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">✏️ 已修改</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {transfer.from_store_name} → {transfer.to_store_name} · 共 {transfer.item_count} 种 ·
                        {new Date(transfer.created_at).toLocaleDateString('zh-CN')}
                        {transfer.paid_amount > 0 && <span className="ml-2 text-green-600">已付 ${parseFloat(transfer.paid_amount).toFixed(2)}</span>}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 flex-wrap gap-1" onClick={e => e.stopPropagation()}>
                    {canEdit(transfer) && (
                      <button onClick={() => openEdit(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-200">
                        <Edit2 className="w-4 h-4" /><span>编辑</span>
                      </button>
                    )}
                    {transfer.is_modified == 1 && (
                      <button onClick={() => openLogs(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-lg text-sm hover:bg-yellow-100 border border-yellow-200">
                        <History className="w-4 h-4" /><span>修改记录</span>
                      </button>
                    )}
                    <button onClick={() => handlePrint(transfer.transfer_no)}
                      className="flex items-center space-x-1 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-200">
                      <Printer className="w-4 h-4" /><span>打印</span>
                    </button>
                    {isHQ && transfer.status === 'pending' && (
                      <button onClick={() => handleShip(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700">
                        <Truck className="w-4 h-4" /><span>确认发货</span>
                      </button>
                    )}
                    {isHQ && transfer.status === 'payment_pending' && (
                      <button onClick={() => handleConfirmPayment(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
                        <DollarSign className="w-4 h-4" /><span>确认收款</span>
                      </button>
                    )}
                    {isManager && isBranch && transfer.status === 'shipped' && transfer.to_store_id === currentUser?.store_id && (
                      <button onClick={() => openReceive(transfer.transfer_no)}
                        className="flex items-center space-x-1 bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700">
                        <CheckCircle className="w-4 h-4" /><span>确认收货</span>
                      </button>
                    )}
                    {isManager && isBranch && transfer.status === 'received' && transfer.to_store_id === currentUser?.store_id && (
                      <button onClick={() => setPayState({ transfer_no: transfer.transfer_no, amount: transfer.total_amount ? parseFloat(transfer.total_amount).toFixed(2) : '' })}
                        className="flex items-center space-x-1 bg-orange-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-orange-600">
                        <CreditCard className="w-4 h-4" /><span>提交付款</span>
                      </button>
                    )}
                    {transfer.status === 'pending' && (
                      <button onClick={() => handleCancel(transfer.transfer_no)}
                        className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-600 hover:bg-gray-50">取消</button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {/* 展开明细 */}
                {isExpanded && (
                  <div className="border-t px-5 py-4 bg-gray-50">
                    {details ? (
                      <>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-gray-500">
                              <th className="text-left py-1">商品名称</th>
                              <th className="text-right py-1">调货单位</th>
                              <th className="text-right py-1">数量（个）</th>
                              <th className="text-right py-1">成本价</th>
                              <th className="text-right py-1">小计</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.map(item => (
                              <tr key={item.id} className="border-t border-gray-100">
                                <td className="py-2">{item.product_name}</td>
                                <td className="text-right py-2">
                                  {item.transfer_unit === 'box' && item.box_count
                                    ? <span className="text-orange-600">{item.box_count} 箱</span>
                                    : <span>{item.quantity} 个</span>}
                                </td>
                                <td className="text-right py-2">{item.quantity} 个</td>
                                <td className="text-right py-2">${parseFloat(item.cost_price).toFixed(2)}</td>
                                <td className="text-right py-2">${parseFloat(item.total_amount).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-gray-200 font-semibold">
                              <td className="py-2" colSpan={2}>合计</td>
                              <td className="text-right py-2">{details.reduce((s, i) => s + i.quantity, 0)} 个</td>
                              <td></td>
                              <td className="text-right py-2">${details.reduce((s, i) => s + parseFloat(i.total_amount), 0).toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                        {transfer.is_modified == 1 && transfer.last_modified_at && (
                          <p className="text-xs text-yellow-600 mt-2">✏️ 最后修改于 {new Date(transfer.last_modified_at).toLocaleString('zh-CN')}</p>
                        )}
                      </>
                    ) : <p className="text-center text-gray-400 py-4">加载中...</p>}
                    {transfer.notes && <p className="text-sm text-gray-500 mt-3">备注：{transfer.notes}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 弹窗 */}
      {showCreate && (
        <CreateDialog products={products} onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}
      {editState && (
        <EditDialog
          transferNo={editState.transfer_no}
          initialItems={editState.items}
          products={products}
          onClose={() => setEditState(null)}
          onEdit={handleEdit}
        />
      )}
      {logsState && (
        <LogsDialog
          transferNo={logsState.transfer_no}
          logs={logsState.logs}
          loading={logsState.loading}
          onClose={() => setLogsState(null)}
        />
      )}
      {receiveState && (
        <ReceiveDialog
          transferNo={receiveState.transfer_no}
          initialItems={receiveState.items}
          onClose={() => setReceiveState(null)}
          onReceive={handleReceive}
        />
      )}
      {payState && (
        <PayDialog
          transferNo={payState.transfer_no}
          defaultAmount={payState.amount}
          onClose={() => setPayState(null)}
          onPay={handlePay}
        />
      )}
    </div>
  );
};

export default StockTransfersPage;
